import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Card, Col, DatePicker, Input, Row, Select, Spin, Table, Tabs, Typography,
} from "antd";
import {
    AuditOutlined, DollarOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined,
    ReloadOutlined, SearchOutlined, WalletOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { formatCurrency, frenchDate, getDaysDurationBetweenDates, getDurationBetweenDates } from "../../variables/functions";
import { allColumns, columns as baseColumns, TAB_CONFIG } from "./components/utils/listData";
// Export au format « État des sinistres déclarés » (mêmes générateurs que /etats/sinistres)
import { generateSinistresDeclaresPDF, generateSinistresDeclaresExcel } from "../stats/sinistre/etatsSinistresFunctions";
// Recherche globale du NavBar (identique à la v1)
import { searchSinistre } from "../../hooks/useSearch";
import { highlightMatch } from "../../variables/stringFunctions";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const PRIMARY = "#242166";

const TABS = [
    { key: "7", label: "Tous les sinistres" },
    { key: "1", label: "En instance" },
    { key: "2", label: "Ouverts" },
    { key: "6", label: "À valider" },
    { key: "3", label: "Refusés" },
    { key: "4", label: "Clôturés" },
    { key: "5", label: "Sans suite" },
    { key: "8", label: "Garanties hors Pool" },
];

const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
const num = (v) => (v != null && v !== "" ? Number(v) : 0);
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 1e9) return `${(v / 1e9).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} Md`;
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M`;
    return v.toLocaleString("fr-FR");
};

/**
 * Listing des sinistres — v2. Cartes KPI cliquables (filtrage instantané), colonnes
 * « Cumul des paiements » + « SAP » après « Évaluation », export PDF/Excel du listing.
 * Chargement complet du jeu filtré (KPI exacts + filtrage client immédiat).
 */
const ListSinistresV2 = () => {
    const navigate = useNavigate();

    const [tabId, setTabId] = useState("7");
    const [allRows, setAllRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [kpiFilter, setKpiFilter] = useState(null); // null | "eval" | "cumul" | "sap"
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filtres serveur
    const [search, setSearch] = useState("");
    // Par défaut : l'année en cours (1er janvier → aujourd'hui). L'utilisateur peut élargir/effacer.
    const [periode, setPeriode] = useState([dayjs().startOf("year"), dayjs()]);
    const [compagnie, setCompagnie] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    // Filtre BRANCHE (client-side : la liste charge tout puis pagine côté navigateur).
    // Chaque ligne porte sa catégorie ; on dérive la branche via le mapping FK catégorie→branche.
    const [selectedBranche, setSelectedBranche] = useState(null);
    const [branchesOptions, setBranchesOptions] = useState([]);
    const [catToBranche, setCatToBranche] = useState({});

    // ── Recherche globale du NavBar (reprise intégrale de la v1) ────────────────
    const [navSearchValue, setNavSearchValue] = useState("");
    const [navResults, setNavResults] = useState([]);

    useEffect(() => {
        if (navSearchValue && navSearchValue.trim().length > 2) {
            searchSinistre(navSearchValue)
                .then((res) => setNavResults(res.data?.content ?? []))
                .catch((err) => console.log(err));
        } else {
            setNavResults([]);
        }
    }, [navSearchValue]);

    // Rendu des résultats dans le dropdown du NavBar — UI carte moderne
    const renderNavSearchResults = (results) => {
        if (!results?.length) {
            return (
                <div style={{ padding: "18px 16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                    Aucun sinistre trouvé pour « {navSearchValue} »
                </div>
            );
        }
        return (
            <div style={{ padding: 6, maxHeight: 420, overflowY: "auto" }}>
                {results.map((item, index) => {
                    const numero = item?.numeroSinistrem || item?.numSinistre || "—";
                    const police = item?.numeroPoliceM || item?.numeroPoliceP || "—";
                    const assure = [item?.prenom, item?.nom].filter(Boolean).join(" ").trim() || "—";
                    const secondaire = [police, frenchDate(item?.dateSinistre), item?.immatriculation]
                        .filter((x) => x && x !== "Invalid date");
                    return (
                        <div
                            key={item?.codeId || index}
                            onClick={() => navigate("/details-sinistre/v2", { state: { codeId: item.codeId } })}
                            style={{
                                display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                                borderRadius: 10, cursor: "pointer", transition: "background .15s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(36,33,102,0.06)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                            <div style={{
                                width: 38, height: 38, flexShrink: 0, borderRadius: 9, background: `${PRIMARY}14`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <FileTextOutlined style={{ color: PRIMARY, fontSize: 17 }} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    <Text strong style={{ color: PRIMARY, fontSize: 13 }}>
                                        {highlightMatch(numero?.toString(), navSearchValue)}
                                    </Text>
                                    <Text style={{ fontSize: 13, color: "#374151" }}>
                                        {highlightMatch(assure, navSearchValue)}
                                    </Text>
                                </div>
                                <Text type="secondary" style={{ fontSize: 12, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {secondaire.map((txt, i) => (
                                        <span key={i}>
                                            {i > 0 && <span style={{ color: "#d1d5db" }}> · </span>}
                                            {highlightMatch(txt?.toString(), navSearchValue)}
                                        </span>
                                    ))}
                                </Text>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    useEffect(() => {
        if (!isPool) return;
        axiosInstance.get("/api/organisme/all/assurances-pool")
            .then((res) => setCompagnies(Array.isArray(res.data) ? res.data : (res.data?.content ?? [])))
            .catch(() => setCompagnies([]));
    }, []);

    // Mapping catégorie → branche (FK réelle) + options du filtre branche.
    useEffect(() => {
        axiosInstance.get("/api/categorie")
            .then((res) => {
                const cats = Array.isArray(res.data) ? res.data : (res.data?.content ?? []);
                const map = {};
                const brs = new Map();
                cats.forEach((c) => {
                    const cat = c?.codeCategorie;
                    const br = c?.branche?.codeBranche;
                    if (cat && br) {
                        map[cat] = br;
                        if (!brs.has(br)) brs.set(br, c?.branche?.nom || br);
                    }
                });
                setCatToBranche(map);
                setBranchesOptions(Array.from(brs, ([value, label]) => ({ value, label }))
                    .sort((a, b) => a.value.localeCompare(b.value)));
            })
            .catch(() => { setCatToBranche({}); setBranchesOptions([]); });
    }, []);

    const buildParams = () => {
        const p = { page: 0, size: 100000, search: search || undefined };
        if (periode && periode[0] && periode[1]) {
            p.startDate = periode[0].format("YYYY-MM-DD");
            p.endDate = periode[1].format("YYYY-MM-DD");
        }
        if (compagnie) p.compagnie = compagnie;
        return p;
    };

    const mapRow = (s, idx) => ({
        key: s.codeId,
        id: idx,
        numeroSinistre: s.numeroSinistrem ?? s.numSinistre ?? "-",
        numeroPolice: s.numeroPoliceM || s.numeroPoliceP || "-",
        assure: s.prenom ? `${s.prenom} ${s.nom}` : (s.nom || "-"),
        organisme: s.organisme,
        dateSinistre: s.dateSinistre ? frenchDate(s.dateSinistre) : "-",
        dateDeclaration: s.dateDeclaration ? frenchDate(s.dateDeclaration) : frenchDate(s.createdAt),
        etat: s.etat,
        categorieCode: s.categorieCode,
        immatriculation: s.immatriculation,
        evaluation: s.evaluation ? `${formatCurrency(s.evaluation)}` : 0,
        evaluationRaw: num(s.evaluation),
        // SAP + cumul règlements viennent désormais DIRECTEMENT de la requête liste (colonnes
        // sap / cumulPaiements) — plus d'appel /sap-cumul sur ~10 000 code_ids (chargement
        // interminable). cf. migration_sinistre_list_sap_cumul.sql.
        cumulPaiements: num(s.cumulPaiements),
        sapValue: num(s.sap),
        duree: getDurationBetweenDates(s.dateSinistre, s.dateDeclaration ?? s.createdAt),
        daysDuree: getDaysDurationBetweenDates(s.dateSinistre, s.dateDeclaration ?? s.createdAt),
        // Renseignés uniquement par l'onglet « Garanties hors Pool » (null ailleurs).
        garantiesHorsPool: s.garantiesHorsPool,
        primeNetteCompagnie: s.primeNetteCompagnie,
    });

    const fetchAll = async (tab = tabId) => {
        const { url } = TAB_CONFIG[tab] || TAB_CONFIG["1"];
        setLoading(true);
        try {
            const res = await axiosInstance.get(url, { params: buildParams() });
            const content = res.data?.content ?? [];
            // sap / cumulPaiements sont déjà portés par chaque ligne (fonction SQL de liste).
            setAllRows(content.map(mapRow));
            setPage(1);
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors du chargement des sinistres.");
            setAllRows([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchAll(tabId); }, [tabId]); // eslint-disable-line

    const applyFilters = () => fetchAll(tabId);

    // Sous-ensemble filtré par BRANCHE (dérivée de la catégorie de la ligne). Applique le filtre
    // à la fois aux KPI et au tableau (et donc à l'export, qui repart des lignes affichées).
    const scopedRows = useMemo(() => {
        const r = allRows || [];
        if (!selectedBranche) return r;
        return r.filter((x) => catToBranche[x.categorieCode] === selectedBranche);
    }, [allRows, selectedBranche, catToBranche]);

    // ── KPI sur l'ensemble chargé (scopé branche) ──────────────────────────────
    const kpis = useMemo(() => {
        const r = scopedRows || [];
        return {
            total: r.length,
            nbEval: r.filter((x) => x.evaluationRaw > 0).length,
            sumEval: r.reduce((a, x) => a + (x.evaluationRaw || 0), 0),
            nbCumul: r.filter((x) => x.cumulPaiements > 0).length,
            sumCumul: r.reduce((a, x) => a + (x.cumulPaiements || 0), 0),
            nbSap: r.filter((x) => x.sapValue > 0).length,
            sumSap: r.reduce((a, x) => a + (x.sapValue || 0), 0),
        };
    }, [scopedRows]);

    // ── Lignes affichées (filtre branche + filtre KPI) ─────────────────────────
    const displayed = useMemo(() => {
        const r = scopedRows || [];
        if (kpiFilter === "eval") return r.filter((x) => x.evaluationRaw > 0);
        if (kpiFilter === "cumul") return r.filter((x) => x.cumulPaiements > 0);
        if (kpiFilter === "sap") return r.filter((x) => x.sapValue > 0);
        return r;
    }, [scopedRows, kpiFilter]);

    const toggleKpi = (k) => { setKpiFilter((prev) => (k === "all" ? null : (prev === k ? null : k))); setPage(1); };

    const tableColumns = useMemo(() => {
        const base = ["1", "2", "3", "4", "5"].includes(tabId) ? baseColumns(navigate) : allColumns(navigate);
        const cols = [...base];
        const extra = [
            { title: "Cumul payé", dataIndex: "cumulPaiements", key: "cumulPaiements", align: "right", width: 120, render: (v) => <Text style={{ whiteSpace: "nowrap" }}>{formatCurrency(v || 0)}</Text> },
            { title: "SAP", dataIndex: "sapValue", key: "sapValue", align: "right", width: 110, render: (v) => <Text strong style={{ color: PRIMARY, whiteSpace: "nowrap" }}>{formatCurrency(v || 0)}</Text> },
        ];
        const idx = cols.findIndex((c) => c.key === "evaluation");
        if (idx >= 0) cols.splice(idx + 1, 0, ...extra); else cols.push(...extra);

        // Onglet « Garanties hors Pool » : on expose la part compagnie du contrat et les
        // garanties concernées (mêmes colonnes que l'onglet homonyme de /production).
        if (tabId === "8") {
            cols.push(
                { title: "Prime compagnie", dataIndex: "primeNetteCompagnie", key: "primeNetteCompagnie", align: "right", width: 130,
                  render: (v) => <Text style={{ whiteSpace: "nowrap" }}>{formatCurrency(v || 0)}</Text> },
                { title: "Garanties hors Pool", dataIndex: "garantiesHorsPool", key: "garantiesHorsPool", width: 240, ellipsis: true,
                  render: (v) => <Text type={v ? undefined : "secondary"}>{v || "—"}</Text> },
            );
        }
        return cols;
    }, [tabId]); // eslint-disable-line

    // ── Export « État des sinistres déclarés » (période sélectionnée + état de l'onglet) ──
    // On repart de l'endpoint d'export riche /api/sinistre/all (DTO SinistreEtatDTO :
    // statut en lettre via mapEtat, évaluation à l'ouverture, SAP, cumul, nature, lieu…),
    // filtré sur les lignes AFFICHÉES (onglet + filtre KPI) par codeId. Le DTO de la liste
    // (SinistreSearchDTO) est trop pauvre (etat numérique, pas d'évaluation à l'ouverture).
    const exportDateArg = () => (periode && periode[0] && periode[1])
        ? [periode[0].format("YYYY-MM-DD"), periode[1].format("YYYY-MM-DD")]
        : ["", ""];

    // Libellé d'état (onglet) reporté dans le sous-titre du document
    const etatLabelExport = () => (TABS.find((t) => t.key === tabId)?.label) || "";

    const fetchExportData = async () => {
        const displayedIds = new Set(displayed.map((m) => m.key));
        const params = {};
        const [d0, d1] = exportDateArg();
        if (d0) { params.startDate = d0; params.endDate = d1; }
        if (compagnie) params.compagnie = compagnie;
        if (search) params.search = search;
        const res = await axiosInstance.get(`/api/sinistre/all`, { params });
        // Restreint au sous-ensemble affiché (onglet + filtre KPI)
        return (res.data || []).filter((r) => displayedIds.has(r.codeId));
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const data = await fetchExportData();
            if (!data.length) { toast.info("Aucun sinistre à exporter pour ces filtres."); return; }
            generateSinistresDeclaresExcel(data, exportDateArg());
        } catch (e) { console.error(e); toast.error("Erreur lors de l'export Excel."); }
        finally { setExporting(false); }
    };

    const handleExportPdf = async () => {
        setExporting(true);
        try {
            const data = await fetchExportData();
            if (!data.length) { toast.info("Aucun sinistre à exporter pour ces filtres."); return; }
            const [d0, d1] = exportDateArg();
            const url = await generateSinistresDeclaresPDF(data, etatLabelExport(), [d0, d1]);
            if (url) {
                const a = document.createElement("a");
                a.href = url;
                a.download = `EtatSinistresDeclares_${d0 || ""}_${d1 || ""}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (e) { console.error(e); toast.error("Erreur lors de l'export PDF."); }
        finally { setExporting(false); }
    };

    const fmtCompagnies = useMemo(() => (compagnies || []).map((c) => ({ value: c.code, label: c.raisonSociale || c.code })), [compagnies]);

    // Branches proposées selon l'organisme « cadre » : le POOL ne gère que GUM + VT ; une
    // COMPAGNIE ne voit que TPV + MOTO. On restreint donc les options du filtre branche en
    // conséquence (non-Pool = compagnie ; Pool = selon la compagnie choisie ; aucune sélection Pool = tout).
    const filteredBranchesOptions = useMemo(() => {
        let allowed = null; // null = toutes les branches
        if (!isPool) {
            allowed = ["TPV", "MOTO"]; // utilisateur d'une compagnie
        } else if (compagnie) {
            const org = (compagnies || []).find((c) => c.code === compagnie);
            if (org?.typeOrganisme === "POOL") allowed = ["GUM", "VT"];
            else if (org) allowed = ["TPV", "MOTO"];
        }
        if (!allowed) return branchesOptions;
        const allow = new Set(allowed);
        return (branchesOptions || []).filter(
            (o) => allow.has(`${o.value}`.toUpperCase()) || allow.has(`${o.label}`.toUpperCase()));
    }, [branchesOptions, compagnie, compagnies]);

    // Réinitialise le filtre branche s'il n'est plus proposé après changement de compagnie.
    useEffect(() => {
        if (selectedBranche && !filteredBranchesOptions.some((o) => o.value === selectedBranche)) {
            setSelectedBranche(null);
        }
    }, [filteredBranchesOptions, selectedBranche]);

    // Carte KPI cliquable
    const KpiCard = ({ k, title, value, sub, icon, color }) => {
        const active = k === "all" ? kpiFilter === null : kpiFilter === k;
        const clickable = !!k;
        return (
            <Card
                bordered={false} hoverable={clickable}
                onClick={clickable ? () => toggleKpi(k) : undefined}
                style={{
                    borderRadius: 14, cursor: clickable ? "pointer" : "default",
                    boxShadow: active ? `0 0 0 2px ${color}` : "0 2px 10px rgba(0,0,0,0.06)",
                    transition: "all .2s",
                }}
                bodyStyle={{ padding: 16 }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}1a`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color, fontSize: 22 }}>{icon}</span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{title}{active ? " · actif" : ""}</Text>
                        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>{value}</div>
                        {sub && <Text type="secondary" style={{ fontSize: 11 }}>{sub}</Text>}
                    </div>
                </div>
            </Card>
        );
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar
                        filteredResults={navResults}
                        renderSearchResults={renderNavSearchResults}
                        searchValue={navSearchValue}
                        setSearchValue={setNavSearchValue}
                    />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{
                                background: `linear-gradient(120deg, ${PRIMARY} 0%, #3b378f 100%)`,
                                borderRadius: 16, padding: "20px 26px", marginBottom: 16,
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                flexWrap: "wrap", gap: 12, boxShadow: "0 6px 18px rgba(36,33,102,0.22)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                    <FileTextOutlined style={{ fontSize: 26, color: "#fff" }} />
                                    <div>
                                        <Title level={3} style={{ color: "#fff", margin: 0 }}>Listing des sinistres</Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>{displayed.length} dossier(s){kpiFilter ? " (filtré)" : ""}</Text>
                                    </div>
                                </div>
                                <Button.Group>
                                    <Button icon={<FilePdfOutlined />} loading={exporting} disabled={!displayed.length} onClick={handleExportPdf}>PDF</Button>
                                    <Button icon={<FileExcelOutlined />} loading={exporting} disabled={!displayed.length} onClick={handleExportExcel}>Excel</Button>
                                </Button.Group>
                            </div>

                            {/* Cartes KPI cliquables */}
                            <Spin spinning={loading}>
                                <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
                                    <Col xs={12} md={6}>
                                        <KpiCard title="Total dossiers" value={kpis.total} sub="cliquer = tout afficher" icon={<FileTextOutlined />} color="#6366f1"
                                            k={kpiFilter ? "__clear__" : null} />
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <KpiCard k="eval" title="Évaluations" value={`${compact(kpis.sumEval)} F`} sub={`${kpis.nbEval} dossier(s) évalué(s)`} icon={<AuditOutlined />} color="#1890ff" />
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <KpiCard k="cumul" title="Cumul payé" value={`${compact(kpis.sumCumul)} F`} sub={`${kpis.nbCumul} dossier(s) payé(s)`} icon={<WalletOutlined />} color="#16a34a" />
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <KpiCard k="sap" title="SAP" value={`${compact(kpis.sumSap)} F`} sub={`${kpis.nbSap} dossier(s) SAP > 0`} icon={<DollarOutlined />} color="#eb2f96" />
                                    </Col>
                                </Row>
                            </Spin>

                            {/* Filtres serveur */}
                            <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={8}>
                                        <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                            placeholder="Rechercher (n° sinistre, police, assuré…)"
                                            value={search} onChange={(e) => setSearch(e.target.value)} onPressEnter={applyFilters} />
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <RangePicker style={{ width: "100%" }} value={periode} onChange={setPeriode} format="DD/MM/YYYY" placeholder={["Début", "Fin"]} allowClear />
                                    </Col>
                                    {isPool && (
                                        <Col xs={24} md={4}>
                                            <Select showSearch allowClear style={{ width: "100%" }} placeholder="Compagnie…" optionFilterProp="label"
                                                value={compagnie || undefined} onChange={(v) => setCompagnie(v || null)} options={fmtCompagnies} />
                                        </Col>
                                    )}
                                    <Col xs={24} md={3}>
                                        {/* Filtre BRANCHE : appliqué instantanément côté client (pas besoin de « Filtrer »). */}
                                        <Select allowClear showSearch style={{ width: "100%" }} placeholder="Branche…" optionFilterProp="label"
                                            value={selectedBranche || undefined}
                                            onChange={(v) => { setSelectedBranche(v || null); setPage(1); }} options={filteredBranchesOptions} />
                                    </Col>
                                    <Col xs={24} md={3}>
                                        <Button type="primary" block icon={<ReloadOutlined />} onClick={applyFilters} style={{ background: PRIMARY, border: "none" }}>Filtrer</Button>
                                    </Col>
                                </Row>
                            </Card>

                            <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                <Tabs activeKey={tabId} onChange={(k) => { setTabId(k); setKpiFilter(null); }} items={TABS.map((t) => ({ key: t.key, label: t.label }))} />
                                <Spin spinning={loading}>
                                    <Table
                                        rowKey="key"
                                        columns={tableColumns}
                                        dataSource={displayed}
                                        size="small"
                                        pagination={{
                                            current: page, pageSize, total: displayed.length, showSizeChanger: true,
                                            showTotal: (t) => `${t} sinistre(s)`,
                                            onChange: (p, s) => { setPage(p); setPageSize(s); },
                                        }}
                                    />
                                </Spin>
                            </Card>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ListSinistresV2;
